export const appMeta = {
  name: 'iPhoneFastFlip',
  version: '1.0.0',
  webUrl: '/iphonefastflip/',
  supportEmail: 'support@alterolis.com',
  marketplaces: ['OLX', 'Allegro Lokalnie', 'Vinted'],
} as const;

export type FeatureId =
  | 'marketplaces'
  | 'profitability'
  | 'priceBooks'
  | 'personalization'
  | 'search'
  | 'favorites'
  | 'details'
  | 'account';

export interface ProductFeature {
  id: FeatureId;
  title: string;
  description: string;
  bullets: string[];
}

export const productFeatures: ProductFeature[] = [
  {
    id: 'marketplaces',
    title: 'Ogłoszenia z trzech źródeł',
    description:
      'Jeden aktualny strumień ofert z OLX, Allegro Lokalnie i Vinted, bez przełączania się między serwisami.',
    bullets: [
      'wspólna lista i spójne karty ofert',
      'sortowanie od najnowszych ogłoszeń',
      'oznaczenie źródła każdej oferty',
    ],
  },
  {
    id: 'profitability',
    title: 'Ocena opłacalności',
    description:
      'Aplikacja zestawia cenę oferty z Twoim cennikiem i pokazuje potencjalny wynik jeszcze przed otwarciem ogłoszenia.',
    bullets: [
      'czytelne oznaczenia okazji i ofert nieopłacalnych',
      'szacowana marża oraz cena odniesienia',
      'uwzględnienie stanu urządzenia',
    ],
  },
  {
    id: 'priceBooks',
    title: 'Własne cenniki iPhone’ów',
    description:
      'Twórz cenniki dla modeli, pamięci i stanu urządzenia. Dodawaj pozycje ręcznie lub importuj je jako JSON.',
    bullets: [
      'osobne ceny dla urządzeń nowych, używanych i uszkodzonych',
      'edycja, scalanie duplikatów i wiele cenników',
      'kopiowanie szablonu i import ze schowka',
    ],
  },
  {
    id: 'personalization',
    title: 'Dopasowane rekomendacje',
    description:
      'Rekomendacje reagują na przeglądane oferty i ustawienia konta, aby najciekawsze pozycje trafiały wyżej.',
    bullets: [
      'profil preferencji użytkownika',
      'historia przeglądania wykorzystywana do dopasowania',
      'sekcja polecanych okazji na pulpicie',
    ],
  },
  {
    id: 'search',
    title: 'Wyszukiwanie i filtry',
    description:
      'Przeszukuj oferty tekstowo, ograniczaj źródła i przełączaj się między wygodną listą a siatką.',
    bullets: [
      'wyszukiwanie po frazie',
      'filtry źródeł i tylko świeże oferty',
      'paginacja oraz doładowywanie kolejnych wyników',
    ],
  },
  {
    id: 'favorites',
    title: 'Ulubione i centrum alertów',
    description:
      'Zapisuj interesujące ogłoszenia na koncie i wracaj do nich z dowolnej obsługiwanej wersji aplikacji.',
    bullets: [
      'synchronizowane ulubione',
      'alerty o nowych okazjach i zmianach cen',
      'ustawienia powiadomień na koncie',
    ],
  },
  {
    id: 'details',
    title: 'Pełne dane ogłoszenia',
    description:
      'Galeria zdjęć, opis, dane sprzedawcy, koszty wysyłki i ochrony kupującego są zebrane w jednym widoku.',
    bullets: [
      'pełnoekranowa galeria',
      'dane specyficzne dla Vinted',
      'bezpośrednie przejście do ogłoszenia źródłowego',
    ],
  },
  {
    id: 'account',
    title: 'Konto, bezpieczeństwo i plany',
    description:
      'Logowanie e-mailem lub Google, weryfikacja e-mail i telefonu, ustawienia profilu oraz obsługa subskrypcji w Stripe.',
    bullets: [
      'sesja konta synchronizowana przez Firebase',
      'zarządzanie planem i portalem rozliczeniowym',
      'plany od bezpłatnego do Business',
    ],
  },
];

export const subscriptionPlans = [
  {
    name: 'Free',
    price: '0 zł',
    description: 'Podstawowe alerty i 5 zapisanych wyszukiwań.',
  },
  {
    name: 'Starter',
    price: '29 zł/mies.',
    description: 'Natychmiastowe powiadomienia i więcej zapisanych wyszukiwań.',
  },
  {
    name: 'Plus',
    price: '39,99 zł/mies.',
    description: 'Wszystko w Starter oraz nielimitowane alerty.',
  },
  {
    name: 'Pro',
    price: '99 zł/mies.',
    description: 'Wszystko w Plus oraz rozszerzona historia cen.',
  },
  {
    name: 'Business',
    price: '199 zł/mies.',
    description: 'Dostęp API, panel zespołowy i dedykowany opiekun.',
  },
] as const;

export const faqItems = [
  {
    question: 'Czy aplikacja webowa ma te same funkcje co Android i Windows?',
    answer:
      'Tak. Web korzysta z tego samego kodu Flutter, ekranów i usług. Na wąskim ekranie pokazuje mobilny układ znany z Androida, a od szerokości 900 px przełącza się na pulpit i nawigację wersji Windows.',
  },
  {
    question: 'Skąd pochodzą ogłoszenia?',
    answer:
      'Aktualnie obsługiwane są OLX, Allegro Lokalnie i Vinted. Każda oferta zachowuje oznaczenie źródła i link do oryginalnego ogłoszenia.',
  },
  {
    question: 'Jak działa ocena opłacalności?',
    answer:
      'Porównuje parametry i cenę ogłoszenia z cenami zapisanymi w Twoim cenniku. Wynik jest wskazówką do dalszej weryfikacji, a nie gwarancją zysku.',
  },
  {
    question: 'Czy muszę mieć płatny plan?',
    answer:
      'Nie. Dostępny jest plan Free. Płatne plany rozszerzają limity, szybkość alertów, historię cen i funkcje zespołowe.',
  },
  {
    question: 'Czy płatność w wersji webowej jest obsługiwana?',
    answer:
      'Tak. Wybór planu, przekierowanie do Stripe Checkout, oczekiwanie na potwierdzenie oraz portal rozliczeniowy korzystają z tego samego systemu co pozostałe wersje aplikacji.',
  },
  {
    question: 'Czy podczas lokalnych testów mogę użyć własnego backendu?',
    answer:
      'Tak. Domyślnie build testowy łączy się z usługą ogłoszeń na porcie 8080 i usługą kont oraz płatności na porcie 8091. Adresy można również zmienić w ustawieniach aplikacji.',
  },
] as const;
